import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useCookie from 'react-use-cookie';
import classnames from 'classnames';

import Button from './Button';
import {
  FormCheckbox,
  FormError,
  FormInput,
  FormTextarea,
  FormSelect,
} from './form';
import LoadingSVG from '../svg/loading.svg';
import MarkdownRenderer from './MarkdownRenderer';
import useSendData from '../hooks/useSendData';

const HubSpotForm = ({ form, params, render }) => {
  const [hubSpotCookie] = useCookie('hubspotutk', null);
  const {
    sendData,
    state: { error: mqlError, result: mqlResult },
  } = useSendData({ endpoint: '/api/create-hubspot-lead' });

  const defaultFieldValues = form.formFieldGroups
    .flatMap((group) =>
      group.fields.filter(({ defaultValue }) => Boolean(defaultValue))
    )
    .map(({ defaultValue, name }) => ({ [name]: defaultValue }));

  const methods = useForm({
    defaultValues: Object.assign({}, params, ...defaultFieldValues),
  });

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = methods;

  const consentFields = form.metaData.find(
    (obj) => obj.name === 'legalConsentOptions'
  );

  const parsedConsentFields = consentFields
    ? JSON.parse(consentFields.value)
    : null;

  const onSubmit = async (values) => {
    const [fields, consent] = Object.entries(values).reduce(
      ([field, consent], [key, value]) =>
        !key.includes('consent')
          ? [[...field, { name: key, value }], consent]
          : [field, [...consent, { name: key, value }]],
      [[], []]
    );

    await sendData({
      consent,
      fields,
      formId: form.id,
      hubSpotCookie,
    });
  };

  const successMessage = mqlResult ? mqlResult.inlineMessage : null;

  if (successMessage)
    return <MarkdownRenderer content={successMessage} />;

  return form.formFieldGroups.length ? (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {form.formFieldGroups.map((group, index) => {
          const formGridColumns = (cols) => {
            switch (cols) {
              case 1:
              default:
                return 'grid-cols-1';
              case 2:
                return 'grid-cols-1 md:grid-cols-2';
              case 3:
                return 'grid-cols-1 md:grid-cols-3';
            }
          };

          return group.fields.length ? (
            <div
              key={index}
              className={classnames(
                'grid gap-x-4',

                formGridColumns(group.fields.length)
              )}
            >
              {group.fields.map((field, index) => {
                const parseFieldType = (type) => {
                  switch (type) {
                    case 'select':
                      return FormSelect;
                    case 'text':
                    default:
                      return FormInput;
                    case 'textarea':
                      return FormTextarea;
                  }
                };

                const Component = parseFieldType(field.fieldType);

                return (
                  <Component key={index} disabled={isSubmitting} {...field} />
                );
              })}
            </div>
          ) : null;
        })}
        {parsedConsentFields?.communicationConsentCheckboxes?.length &&
          parsedConsentFields.communicationConsentCheckboxes.map((field) => (
            <FormCheckbox
              key={field.communicationTypeId}
              name={`consent-${field.communicationTypeId.toString()}`}
              label={field.label}
              required={field.required}
              disabled={isSubmitting}
            />
          ))}
        {mqlError && <FormError className="mb-2">{mqlError}</FormError>}
        {render ? (
          render(isSubmitting)
        ) : (
          <Button colour="purple" disabled={isSubmitting}>
            {isSubmitting && <LoadingSVG className="fill-current mr-2 w-6" />}
            Submit
          </Button>
        )}
      </form>
    </FormProvider>
  ) : null;
};

HubSpotForm.Button = Button;

export default HubSpotForm;
